import { getFirebaseStorage } from "@/lib/firebase";
import { ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import { sanitizeFilename } from "@/lib/filename-utils";

export interface PdfUploadResult {
  storagePath: string;
  fileName: string;
}

/**
 * Upload PDF file to Firebase Storage with progress callback
 *
 * @param pdfFile - The PDF file to upload
 * @param userId - User ID for storage path
 * @param conversationId - Conversation ID for storage path
 * @param onProgress - Optional callback for upload progress (0-100)
 * @param originalFileName - Optional original filename to preserve (will be sanitized)
 * @returns Upload result with storage path and fileName
 */
export async function uploadPdfToStorage(
  pdfFile: File | Blob,
  userId: string,
  conversationId: string,
  onProgress?: (progress: number) => void,
  originalFileName?: string,
): Promise<PdfUploadResult> {
  const storage = getFirebaseStorage();

  const fileName = sanitizeFilename(originalFileName || "document.pdf");
  const storagePath = `users/${userId}/projects/${conversationId}/pdfs/${fileName}`;

  const storageRef = ref(storage, storagePath);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, pdfFile, {
      contentType: "application/pdf",
    });

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(Math.round(progress));
      },
      (error) => {
        reject(error);
      },
      () => {
        resolve({
          storagePath: `pdfs/${fileName}`,
          fileName,
        });
      },
    );
  });
}

/**
 * Delete PDF file from Firebase Storage
 *
 * @param userId - User ID for storage path
 * @param conversationId - Conversation ID for storage path
 * @param fileName - The file name to delete
 */
export async function deletePdfFromStorage(
  userId: string,
  conversationId: string,
  fileName: string,
): Promise<void> {
  const storage = getFirebaseStorage();
  const storagePath = `users/${userId}/projects/${conversationId}/pdfs/${fileName}`;
  const storageRef = ref(storage, storagePath);

  await deleteObject(storageRef);
}
